import { fromGeoJSON } from '@mapbox/polyline';
import moment from 'moment';
import { DropZone, Report, Review, periodToParams, toSource } from '../models';
import { AppService } from './app';
import { HttpService } from './http';
import { backendReportTypesCodes, reportTypesCodes } from './report-type';
import { UserService } from './user';
export class ReportService {
  static async addReport({
    type,
    place,
    point,
    geometry,
    description,
    scheduleDates,
    photos,
    parkingCapacity,
    canCycleThrough,
    canCargoCycleThrough,
    syncToOsm
  }) {
    try {
      if (type < 1) throw new Error(`invalid report type id: ${type}`);
      let params = {
        type,
        is_temporary: scheduleDates ? scheduleDates[1] !== null : false
      };
      if (place) {
        params = {
          ...params,
          geo_point: place.point,
          address: place.address
        };
      } else if (point) params.geo_point = point;
      if (geometry) params.geometry = geometry;
      if (description) params.description = description;
      if (parkingCapacity) params.bicycle_parking_capacity = parkingCapacity;
      if (typeof canCycleThrough === 'boolean') params.can_cycle_through = canCycleThrough;
      if (typeof canCargoCycleThrough === 'boolean') params.can_cargobicycle_through = canCargoCycleThrough;
      if (syncToOsm === false) params.sync_to_osm = false;
      if (scheduleDates && scheduleDates[0]) params.start_date = moment(scheduleDates[0]).format('YYYY-MM-DDTHH:mm');
      if (scheduleDates && scheduleDates[1]) params.end_date = moment(scheduleDates[1]).format('YYYY-MM-DDTHH:mm');
      const data = await HttpService.post('v4', '/reports', [], [], JSON.stringify(params));
      if (photos && photos.length > 0) {
        data.photos = await Promise.all(photos.map(photo => {
          const formData = new FormData();
          formData.append('photo', photo);
          return HttpService.post('v4', `/reports/${data.id}/photos`, [], [], formData);
        }));
      }
      const report = parseReport(data, UserService.currentUser?.id, AppService.environment.backendUrl);
      return report;
    } catch (err) {
      console.error('[ReportService][addReport]', err);
      throw err;
    }
  }
  static async addReview({
    reportId,
    comment,
    rating,
    photos
  }) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const params = {
        report: reportId
      };
      if (rating) params.rating = rating;
      if (comment) params.comment = comment;
      const data = await HttpService.post('v2', '/reviews', [], [], JSON.stringify(params));
      if (photos && photos.length > 0) {
        data.photos = await Promise.all(photos.map(photo => {
          const formData = new FormData();
          formData.append('photo', photo);
          return HttpService.post('v2', `/reviews/${data.id}/photos`, [], [], formData);
        }));
      }
      const review = parseReview(data, UserService.currentUser.id, AppService.environment.backendUrl);
      return review;
    } catch (err) {
      console.error('[ReportService][addReview]', err);
      throw err;
    }
  }
  static async updateReview(id, {
    comment,
    rating
  }) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const params = {};
      if (rating) params.rating = rating;
      if (comment) params.comment = comment;
      const data = await HttpService.put('v2', `/reviews/${id}`, [], [], JSON.stringify(params));
      const review = parseReview(data, UserService.currentUser.id, AppService.environment.backendUrl);
      return review;
    } catch (err) {
      console.error('[ReportService][updateReview]', err);
      throw err;
    }
  }
  static async deleteReview(id) {
    try {
      await HttpService.delete('v2', `/reviews/${id}`);
      return true;
    } catch (err) {
      console.error('[ReportService][deleteReview]', err);
      throw err;
    }
  }
  static async getReports({
    bounds,
    period,
    typeCodes,
    status,
    ordering,
    page,
    rowsPerPage,
    query,
    cyclabilityZoneId,
    sources,
    reviewCountMax,
    reviewCountMin,
    ongoingDateStart,
    ongoingDateEnd,
    highPriorityContributor
  }) {
    try {
      const params = [];
      if (bounds) {
        const {
          north,
          east,
          south,
          west
        } = bounds;
        params.push({
          key: 'north',
          value: north
        }, {
          key: 'east',
          value: east
        }, {
          key: 'south',
          value: south
        }, {
          key: 'west',
          value: west
        });
      }
      if (period) params.push(...periodToParams(period));
      if (ongoingDateStart) params.push({
        key: 'ongoing_report_date_start',
        value: ongoingDateStart.format('YYYY-MM-DD')
      });
      if (ongoingDateEnd) params.push({
        key: 'ongoing_report_date_end',
        value: ongoingDateEnd.format('YYYY-MM-DD')
      });
      if (typeCodes && typeCodes.length > 0) params.push({
        key: 'type_code',
        value: typeCodes.flatMap(code => backendReportTypesCodes[code]).join(',')
      });
      if (sources && sources.length === 1) {
        if (sources[0] === 'geovelo') params.push({
          key: 'no_opendata',
          value: true
        });else if (sources[0] === 'openData') params.push({
          key: 'opendata_only',
          value: true
        });
      }
      if (ordering) params.push({
        key: 'ordering',
        value: ordering
      });
      if (page) {
        params.push({
          key: 'page',
          value: page
        });
        if (rowsPerPage) params.push({
          key: 'page_size',
          value: rowsPerPage
        });
      }
      if (status && status.length > 0) {
        params.push({
          key: 'status',
          value: status.map(status => status).join(',')
        });
      }
      if (query) params.push({
        key: 'query',
        value: query
      });
      if (cyclabilityZoneId) params.push({
        key: 'cyclability_zone_id',
        value: cyclabilityZoneId
      });
      if (reviewCountMax !== undefined) params.push({
        key: 'review_count_max',
        value: reviewCountMax
      });
      if (reviewCountMin !== undefined) params.push({
        key: 'review_count_min',
        value: reviewCountMin
      });
      if (highPriorityContributor) params.push({
        key: 'high_priority_contributor',
        value: highPriorityContributor
      });
      const {
        count,
        next,
        results
      } = await HttpService.get('v4', '/reports', params);
      const reports = results.map(data => parseReport(data, UserService.currentUser?.id, AppService.environment.backendUrl));
      return {
        count,
        next,
        reports
      };
    } catch (err) {
      console.error('[ReportService][getReports]', err);
      throw Array.isArray(err) ? new Error(err[0]) : err;
    }
  }
  static async getUserReports({
    userId,
    ordering,
    page,
    rowsPerPage,
    query
  }) {
    try {
      const params = [{
        key: 'ordering',
        value: ordering || '-id'
      }];
      if (page) {
        params.push({
          key: 'page',
          value: page
        });
        if (rowsPerPage) params.push({
          key: 'page_size',
          value: rowsPerPage
        });
      }
      if (query) params.push({
        key: 'query',
        value: query
      });
      const {
        count,
        next,
        results
      } = await HttpService.get('v4', `/users/${userId}/reports`, params);
      const reports = results.map(data => parseReport(data, UserService.currentUser?.id, AppService.environment.backendUrl));
      return {
        count,
        next,
        reports
      };
    } catch (err) {
      console.error('[ReportService][getUserReports]', err);
      throw Array.isArray(err) ? new Error(err[0]) : err;
    }
  }
  static async getUserReportStats(userId) {
    try {
      const data = await HttpService.get('v4', `/users/${userId}/report_stats`);
      return {
        totalReports: data.total_reports || 0,
        totalComments: data.total_comments || 0,
        totalStandby: data.total_standby || 0,
        totalValidated: data.total_validated || 0,
        totalEarnedPoints: data.total_earned_points || 0
      };
    } catch (err) {
      console.error('[ReportService][getUserReportStats]', err);
      throw err;
    }
  }
  static async getRouteReports({
    geometry,
    geometryBuffer,
    period,
    typeCodes,
    status,
    ordering,
    page,
    rowsPerPage,
    query,
    cyclabilityZoneId,
    sources,
    reviewCountMax,
    reviewCountMin,
    ongoingDateStart,
    ongoingDateEnd
  }) {
    if (!geometry || geometry.coordinates.length < 2) return {
      count: 0,
      next: null,
      reports: []
    };
    try {
      const params = [{
        key: 'geometry_buffer',
        value: geometryBuffer || 50
      }];
      if (period) params.push(...periodToParams(period));
      if (ongoingDateStart) params.push({
        key: 'ongoing_report_date_start',
        value: ongoingDateStart.format('YYYY-MM-DD')
      });
      if (ongoingDateEnd) params.push({
        key: 'ongoing_report_date_end',
        value: ongoingDateEnd.format('YYYY-MM-DD')
      });
      if (typeCodes && typeCodes.length > 0) params.push({
        key: 'type_code',
        value: typeCodes.flatMap(code => backendReportTypesCodes[code]).join(',')
      });
      if (sources && sources.length === 1) {
        if (sources[0] === 'geovelo') params.push({
          key: 'no_opendata',
          value: true
        });else if (sources[0] === 'openData') params.push({
          key: 'opendata_only',
          value: true
        });
      }
      if (ordering) params.push({
        key: 'ordering',
        value: ordering
      });
      if (page) params.push({
        key: 'page',
        value: page
      });
      if (rowsPerPage) params.push({
        key: 'page_size',
        value: rowsPerPage
      });
      if (status && status.length > 0) params.push({
        key: 'status',
        value: status.map(status => status).join(',')
      });
      if (query) params.push({
        key: 'query',
        value: query
      });
      if (cyclabilityZoneId) params.push({
        key: 'cyclability_zone_id',
        value: cyclabilityZoneId
      });
      if (reviewCountMax !== undefined) params.push({
        key: 'review_count_max',
        value: reviewCountMax
      });
      if (reviewCountMin !== undefined) params.push({
        key: 'review_count_min',
        value: reviewCountMin
      });
      const {
        count,
        next,
        results
      } = await HttpService.post('v4', '/reports_on_route', params, [], JSON.stringify({
        geometry: fromGeoJSON(geometry, 6)
      }));
      const reports = results.map(data => parseReport(data, UserService.currentUser?.id, AppService.environment.backendUrl));
      return {
        count,
        next,
        reports
      };
    } catch (err) {
      console.error('[ReportService][getRouteReports]', err);
      throw Array.isArray(err) ? new Error(err[0]) : err;
    }
  }
  static async getReport(id) {
    try {
      const data = await HttpService.get('v4', `/reports/${id}`);
      return parseReport(data, UserService.currentUser?.id, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[ReportService][getReport]', err);
      throw err;
    }
  }
  static async updateReport(reportToUpdate, {
    description,
    geometry,
    isValid,
    point,
    status,
    type,
    scheduleDates
  }) {
    try {
      const params = {};
      if (description) params.description = description;
      if (status) params.status = status;
      if (type) {
        if (type.id < 1) throw new Error(`invalid report type id: ${type.id}`);
        params.type = type.id;
      }
      if (typeof isValid === 'boolean') params.is_valid = true;
      if (geometry) params.geometry = geometry;
      if (point) params.geo_point = point;
      if (scheduleDates && scheduleDates[0]) params.start_date = moment(scheduleDates[0]).format('YYYY-MM-DDTHH:mm');
      if (scheduleDates && scheduleDates[1]) params.end_date = moment(scheduleDates[1]).format('YYYY-MM-DDTHH:mm');
      const data = await HttpService.patch('v4', `/reports/${reportToUpdate.id}`, [], [], JSON.stringify(params));
      return parseReport(data, UserService.currentUser?.id, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[ReportService][updateReport]', err);
      throw err;
    }
  }
  static async deleteReport(id) {
    try {
      await HttpService.delete('v2', `/reports/${id}`);
      return true;
    } catch (err) {
      console.error('[ReportService][deleteReport]', err);
      throw err;
    }
  }
  static async addDropZone({
    reportId,
    title,
    description,
    point,
    geometry,
    capacity
  }) {
    try {
      const formData = new FormData();
      if (title) formData.append('title', title);
      if (description) formData.append('description', description);
      formData.append('point', JSON.stringify(point));
      if (geometry) formData.append('geometry', JSON.stringify(geometry));
      if (capacity) formData.append('capacity', capacity.toString());
      const data = await HttpService.post('v3', `/reports/${reportId}/drop_zones`, [], [], formData);
      return parseDropZone(data);
    } catch (err) {
      console.error('[ReportService][addDropZone]', err);
      throw err;
    }
  }
  static async updateDropZone({
    reportId,
    dropZoneId,
    title,
    description,
    point,
    geometry,
    capacity
  }) {
    try {
      const formData = new FormData();
      if (title) formData.append('title', title);
      if (description) formData.append('description', description);
      formData.append('geo_point', JSON.stringify(point));
      if (geometry) formData.append('geometry', JSON.stringify(geometry));
      if (capacity) formData.append('capacity', capacity.toString());
      const data = await HttpService.patch('v3', `/reports/${reportId}/drop_zones/${dropZoneId}`, [], [], formData);
      return parseDropZone(data);
    } catch (err) {
      console.error('[ReportService][updateDropZone]', err);
      throw err;
    }
  }
  static async deleteDropZone(reportId, dropZoneId) {
    try {
      await HttpService.delete('v3', `/reports/${reportId}/drop_zones/${dropZoneId}`);
      return true;
    } catch (err) {
      console.error('[ReportService][deleteDropZone]', err);
      throw err;
    }
  }
}
function parseReview({
  id,
  created,
  creator,
  comment,
  rating,
  is_creator: isCreator,
  photos
}, userId, backendUrl) {
  return new Review(id, moment(created), typeof creator === 'string' ? {
    id: isCreator ? userId : undefined,
    username: creator
  } : creator, comment, rating, photos?.map(({
    id,
    photo
  }) => ({
    id,
    url: `${backendUrl}${photo}`
  })) || []);
}
function parseReport({
  id,
  created,
  updated,
  creator,
  description,
  geo_point,
  address,
  photos,
  source,
  status,
  report_type_code,
  reviews,
  start_date,
  end_date,
  is_temporary,
  osm_note_id,
  computed_route_id,
  report_source,
  is_valid,
  city_name,
  city_reference,
  geometry,
  drop_zones,
  review_count,
  positive_rating,
  negative_rating
}, userId, backendUrl) {
  const isCreatorHighPriorityContributor = typeof creator === 'object' && Boolean(creator?.high_priority_contributor);
  const creatorId = typeof creator === 'object' ? creator?.id : undefined;
  return new Report(id, moment(created), updated ? moment(updated) : moment(created), (typeof creator === 'string' ? creator : creator?.username) || '', description, geo_point, address, city_name || city_reference ? {
    name: city_name || null,
    reference: city_reference || null
  } : undefined, photos?.map(({
    id,
    photo
  }) => ({
    id,
    url: `${backendUrl}${photo}`
  })) || [], toSource(source), status, reportTypesCodes[report_type_code], (reviews || []).map(data => parseReview(data, userId, backendUrl)), start_date ? moment(start_date) : null, end_date ? moment(end_date) : null, is_temporary, osm_note_id, computed_route_id, report_source, Boolean(is_valid), geometry && geometry.type === 'Polygon' ? {
    type: 'MultiPolygon',
    coordinates: [geometry.coordinates]
  } : geometry, drop_zones?.map(dropZone => parseDropZone(dropZone)) || [], isCreatorHighPriorityContributor, creatorId, review_count, positive_rating, negative_rating);
}
function parseDropZone({
  id,
  created,
  updated,
  title,
  description,
  point,
  geometry,
  capacity
}) {
  return new DropZone(id, moment(created), moment(updated), title, description, point, geometry, capacity);
}